import { useState, useCallback } from 'react';

/**
 * This is a higher order function that generates a custom hook function which binds `key` to
 * an external storage adapter and react state.
 *
 * The generic type T should be inferred from the KeyValueAdapter supplied on use.
 *
 * @param key The key to which this external storage is bound
 * @param adapter The adapter used to interface with the external storage
 */
var composeExternalStorageHook = function (key, adapter) {
    return function () {
        var _a = useState(function () { return adapter.get(key); }), val = _a[0], setVal = _a[1];
        // curried function because we are memoizing the function, not the result.
        var removeProxy = useCallback(function () {
            adapter.remove(key);
            setVal(null);
        }, [adapter, key]);
        var setProxy = useCallback(function (newVal) {
            adapter.set(key, newVal);
            setVal(newVal);
        }, [adapter, key, setVal]);
        return [val, setProxy, removeProxy];
    };
};

/**
 * Adapter for use with custom hooks provided by @amtrav/external-storage-helpers. This maps
 * a single key within localStorage to a value that is bound as a representation of a react state variable.
 */
var localStorageAdapter$1 = {
    set: function (key, val) {
        // undefined values in localStorage need to be removed
        if (val === undefined) {
            return;
        }
        var serializedValue = JSON.stringify(val);
        window.localStorage.setItem(key, serializedValue);
    },
    get: function (key) {
        return window.localStorage.getItem(key);
    },
    remove: function (key) { return window.localStorage.removeItem(key); },
};
/**
 * Composes a storage hook for a localStorage value using the localStorageAdapter. This hook
 * will only work for one key inside the localStorage object.
 *
 * @param {string} key
 */
var useLocalStorageHook = function (key) { return composeExternalStorageHook(key, localStorageAdapter$1)(); };

/**
 * Adapter for use with custom hooks provided by @amtrav/external-storage-helpers. This maps
 * a single key within sessionStorage to a value that is bound as a representation of a react state variable.
 */
var sessionStorageAdapter$1 = {
    set: function (key, val) {
        // undefined values in sessionStorage need to be removed
        if (val === undefined) {
            return;
        }
        var serializedValue = JSON.stringify(val);
        window.sessionStorage.setItem(key, serializedValue);
    },
    get: function (key) {
        return window.sessionStorage.getItem(key);
    },
    remove: function (key) { return window.sessionStorage.removeItem(key); },
};
/**
 * Composes a storage hook for a sessionStorage value using the sessionStorageAdapter. This hook
 * will only work for one key inside the sessionStorage object.
 *
 * @param {string} key
 */
var useSessionStorageHook = function (key) { return composeExternalStorageHook(key, sessionStorageAdapter$1)(); };

var simpleDatePattern$1 = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
var dateReviver$1 = function (_, val) {
    if (typeof val === 'string' && val.match(simpleDatePattern$1)) {
        try {
            return new Date(val);
        }
        catch (err) {
            return val;
        }
    }
    return val;
};
/**
 * Adapter for use with custom hooks provided by @amtrav/external-storage-helpers. This maps
 * a single key within localStorage to a value that is bound as a representation of a react state variable.
 *
 * localStorageAdapter is very general and should work almost with type 'any'. We use a generic so that users
 * can set a more specific type on use.
 */
var localStorageAdapter = {
    set: function (key, val) {
        // undefined values in localStorage need to be removed
        if (val === undefined) {
            return;
        }
        var serializedValue = JSON.stringify(val);
        window.localStorage.setItem(key, serializedValue);
    },
    get: function (key) {
        var raw = window.localStorage.getItem(key);
        try {
            return raw ? JSON.parse(raw, dateReviver$1) : null;
        }
        catch (err) {
            console.warn('Unparsable string found', raw);
        }
        return null;
    },
    remove: function (key) { return window.localStorage.removeItem(key); },
};
/**
 * Composes a storage hook for a localStorage value using the localStorageAdapter. This hook
 * will only work for one key inside the localStorage object.
 *
 * This hook is generic, so users will need to specify a type on use, ie `useLocalStorageJsonHook<MyCustomType>(myKey)`
 * This will let TypeScript know the type of value this hook can store and retrieve.
 *
 * @param {string} key
 */
var useLocalStorageJsonHook = function (key) { return composeExternalStorageHook(key, localStorageAdapter)(); };

var simpleDatePattern = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
var dateReviver = function (_, val) {
    if (typeof val === 'string' && val.match(simpleDatePattern)) {
        try {
            return new Date(val);
        }
        catch (err) {
            return val;
        }
    }
    return val;
};
/**
 * Adapter for use with custom hooks provided by @amtrav/external-storage-helpers. This maps
 * a single key within sessionStorage to a value that is bound as a representation of a react state variable.
 *
 * sessionStorageAdapter is very general and should work almost with type 'any'. We use a generic so that users
 * can set a more specific type on use.
 */
var sessionStorageAdapter = {
    set: function (key, val) {
        // undefined values in sessionStorage need to be removed
        if (val === undefined) {
            return;
        }
        var serializedValue = JSON.stringify(val);
        window.sessionStorage.setItem(key, serializedValue);
    },
    get: function (key) {
        var raw = window.sessionStorage.getItem(key);
        try {
            return raw ? JSON.parse(raw, dateReviver) : null;
        }
        catch (err) {
            console.warn('Unparsable string found', raw);
        }
        return null;
    },
    remove: function (key) { return window.sessionStorage.removeItem(key); },
};
/**
 * Composes a storage hook for a sessionStorage value using the sessionStorageAdapter. This hook
 * will only work for one key inside the sessionStorage object.
 *
 * This hook is generic, so users will need to specify a type on use, ie `useSessionStorageJsonHook<MyCustomType>(myKey)`
 * This will let TypeScript know the type of value this hook can store and retrieve.
 *
 * @param {string} key
 */
var useSessionStorageJsonHook = function (key) {
    return composeExternalStorageHook(key, sessionStorageAdapter)();
};

export { composeExternalStorageHook, localStorageAdapter$1 as localStorageAdapter, sessionStorageAdapter$1 as sessionStorageAdapter, useLocalStorageHook, useLocalStorageJsonHook, useSessionStorageHook, useSessionStorageJsonHook };
